import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useAnnouncement from '../data/useAnnouncement';
import useModalBehavior from './useModalBehavior';
import { imageUrl } from '../data/imageUrl';
import { categoryImageUrl } from '../data/categoryImageUrl';
import { responsiveImage, CARD_WIDTHS } from '../data/responsiveImage';
import { GOLD, GOLD_LT, CREAM, INK } from '../theme';

// Was 20s, which reliably landed mid-scroll — the visitor was reading a
// section and the modal took the page away from them. Firing while they're
// still near the top means it interrupts orientation, not engagement.
const DELAY_MS = 6000;
const EXPIRY_MS = 14 * 24 * 60 * 60 * 1000; // an item stays "new" for 14 days
const SESSION_KEY = 'ij-whatsnew-shown';

export default function WhatsNewModal() {
  const { pathname } = useLocation();
  const { announcement } = useAnnouncement();
  const [open, setOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const panelRef = useRef(null);

  const isAdmin = pathname.startsWith('/admin');
  const expired = announcement && Date.now() - new Date(announcement.publishedAt).getTime() > EXPIRY_MS;
  const alreadyShown = typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === '1';
  const eligible = !isAdmin && announcement && !expired && !alreadyShown;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!eligible) return undefined;
    const timer = setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, '1');
      setOpen(true);
    }, DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  // Escape to close, Tab trapped inside, body scroll locked, focus restored.
  useModalBehavior(open, close, panelRef);

  if (!open || !announcement) return null;

  const src = announcement.image
    ? (announcement.type === 'collection' ? imageUrl(announcement.image) : categoryImageUrl(announcement.image))
    : '';

  return (
    <div
      role="presentation"
      onClick={close}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,7,4,.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 20 }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ij-whatsnew-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', background: INK, border: `1px solid ${GOLD}`, maxWidth: 420, width: '100%', fontFamily: "'Cormorant Garamond', serif", overflow: 'hidden', outline: 'none' }}
      >
        <button
          onClick={close}
          aria-label="Close"
          style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(10,7,4,.55)', border: '1px solid rgba(201,162,75,.5)', color: CREAM, fontSize: 15, lineHeight: 1, width: 34, height: 34, cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}
        >
          ✕
        </button>

        {src && (
          // The aspect box is reserved up front and carries the same woven
          // stripe used elsewhere for pending images, so a slow blob fetch
          // shows the house placeholder rather than an empty brown rectangle.
          <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 5', background: 'repeating-linear-gradient(135deg,#2a2016,#2a2016 14px,#231a11 14px,#231a11 28px)' }}>
            <img
              {...responsiveImage(src, CARD_WIDTHS, '(max-width: 460px) 92vw, 420px')}
              alt={announcement.title}
              decoding="async"
              onLoad={() => setImgLoaded(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block', opacity: imgLoaded ? 1 : 0, transition: 'opacity .5s ease' }}
            />
          </div>
        )}

        <div style={{ padding: 'clamp(22px,4vw,32px)' }}>
          <div style={{ fontWeight: 600, fontSize: 12, letterSpacing: '.28em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 10 }}>What's New</div>
          <h3 id="ij-whatsnew-title" style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: 'italic', fontWeight: 500, fontSize: 28, margin: '0 0 6px', color: CREAM }}>{announcement.title}</h3>
          {announcement.tagline && (
            <p style={{ fontSize: 14, color: 'rgba(239,228,210,.65)', margin: '0 0 20px' }}>{announcement.tagline}</p>
          )}
          {/* Was `View {title}` inside an uppercase button, which turned
              "Tight piece" into "VIEW TIGHT PIECE". The name already sits in
              the heading directly above; the button only needs the verb. */}
          <Link
            to={announcement.href}
            onClick={close}
            style={{ display: 'inline-block', fontWeight: 600, fontSize: 13, letterSpacing: '.18em', textTransform: 'uppercase', padding: '14px 28px', background: GOLD, color: INK, textDecoration: 'none' }}
          >
            View the piece →
          </Link>
        </div>
      </div>
    </div>
  );
}
